import React, { useState, useMemo, useEffect } from "react";
import styles from "./Nudge.module.css";
import {
  IconBulb,
  IconInfoCircle,
  IconPrescription,
  IconClipboardText,
  IconFirstAidKit,
  IconCoinRupee,
} from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import { MDocumentUploadOptions } from "@/components/ui/molecules/MDocumentUploadOptions";
import { getMemberInfo } from "@/hooks/useMembers";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { membersService } from "@/services/members.service";

interface NudgeProps {
  onYesClick?: () => void;
}

const NUDGE_INTERVAL_DAYS = 14;
const NUDGE_DISMISSED_KEY = "maatra_nudge_dismissed";

// Helper function to format date as "13 Feb"
const formatLastAccessDate = (dateValue: string | undefined): string => {
  if (!dateValue) {
    // Treat no dateValue as today's date
    dateValue = new Date().toISOString();
  }

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "recently";

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });

  return `${day} ${month}`;
};

// Helper function to check if 14+ days have passed since last access
const checkInactivityDays = (lastAccessDate: string | undefined): number => {
  if (!lastAccessDate) return 0;

  const lastDate = new Date(lastAccessDate);
  if (isNaN(lastDate.getTime())) return 0;

  const now = new Date();
  const diffTime = now.getTime() - lastDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

// Helper function to check if nudge was dismissed today
const wasDismissedToday = (memberId: string): boolean => {
  try {
    const storageKey = `${NUDGE_DISMISSED_KEY}_${memberId}`;
    const dismissedDateStr = localStorage.getItem(storageKey);

    if (!dismissedDateStr) return false;

    const dismissedDate = new Date(dismissedDateStr);
    const today = new Date();

    // Check if dismissed date is today
    return dismissedDate.toDateString() === today.toDateString();
  } catch (error) {
    console.error("Error checking nudge dismissal:", error);
    return false;
  }
};

// Helper function to mark nudge as dismissed today
const markDismissedToday = (memberId: string): void => {
  try {
    const storageKey = `${NUDGE_DISMISSED_KEY}_${memberId}`;
    localStorage.setItem(storageKey, new Date().toISOString());
  } catch (error) {
    console.error("Error marking nudge as dismissed:", error);
  }
};

const Nudge: React.FC<NudgeProps> = ({ onYesClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [lastAccessDate, setLastAccessDate] = useState<string | undefined>(
    undefined
  );

  // Get member info to access lastAccessDate
  const memberInfo = getMemberInfo();

  // Format the last access date dynamically using fresh data from backend
  const formattedLastVisitDate = useMemo(() => {
    return formatLastAccessDate(lastAccessDate);
  }, [lastAccessDate]);

  // Check on mount if nudge should be shown
  useEffect(() => {
    const checkAndShowNudge = async () => {
      // Check if already dismissed today
      if (wasDismissedToday(memberInfo.id)) {
        setShouldShow(false);
        return;
      }

      // Fetch fresh member data from backend to get latest lastAccessDate
      let freshLastAccessDate: string | undefined;
      try {
        const freshMemberData = await membersService.getMember(memberInfo.id);
        freshLastAccessDate = freshMemberData.lastAccessDate;
      } catch (error) {
        console.error("Error fetching member data:", error);
        // Fallback to cached data if fetch fails
        freshLastAccessDate = memberInfo.lastAccessDate;
      }

      // Store fresh lastAccessDate in state for display
      setLastAccessDate(freshLastAccessDate);

      // Check if user has been inactive for 14+ days using fresh data
      const inactiveDays = checkInactivityDays(freshLastAccessDate);

      if (inactiveDays >= NUDGE_INTERVAL_DAYS) {
        // Show nudge - don't update lastAccessDate yet
        setShouldShow(true);
      } else {
        // User is active, update lastAccessDate via API
        try {
          await membersService.updateLastAccessDate();
        } catch (error) {
          console.error("Error updating last access date:", error);
        }
        setShouldShow(false);
      }
    };

    checkAndShowNudge();
  }, [memberInfo.id]);

  const handleYesClick = () => {
    setIsExpanded(true);
    onYesClick?.();
  };

  const handleDismiss = async () => {
    setIsDismissed(true);

    // Mark as dismissed today in localStorage
    markDismissedToday(memberInfo.id);

    // Update lastAccessDate via API
    try {
      await membersService.updateLastAccessDate();
    } catch (error) {
      console.error("Error updating last access date on dismiss:", error);
    }
  };

  const handleAddDocument = () => {
    setShowUploadModal(true);
  };

  const handleUploadModalClose = async () => {
    setShowUploadModal(false);

    // Dismiss nudge after user has interacted with document upload
    // This assumes they've taken action on the health update
    await handleDismiss();
  };

  const updateCards = [
    {
      icon: IconPrescription,
      title: "There was a doctor visit",
      cta: "Add the prescription",
    },
    {
      icon: IconClipboardText,
      title: "Some medical tests were done",
      cta: "Add the report",
    },
    {
      icon: IconFirstAidKit,
      title: "There was a procedure or treatment",
      cta: "Add the details",
    },
    {
      icon: IconCoinRupee,
      title: "Medicines were purchased",
      cta: "Add the bills",
    },
  ];

  // Don't render the nudge if it hasn't been 10+ days or if dismissed
  if (!shouldShow || isDismissed) {
    return null;
  }

  return (
    <>
      <MSpacer mt={24} />
      <div
        className={`${styles.nudgeContainer} ${
          isExpanded ? styles.expanded : ""
        }`}
      >
        {!isExpanded ? (
          <>
            <div className={styles.lastVisitText}>
              You were last on Maatra on {formattedLastVisitDate}
            </div>

            <div className={styles.questionContainer}>
              <div className={styles.iconContainer}>
                <IconBulb size={32} color="#8764BB" />
              </div>
              <div className={styles.questionText}>
                Have there been any health updates since then?
              </div>
            </div>

            <div className={styles.buttonContainer}>
              <MButton
                text="No"
                size="sm"
                onClick={handleDismiss}
                className={styles.noButton}
              />
              <MButton
                text="Yes"
                size="sm"
                onClick={handleYesClick}
                className={styles.yesButton}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.lastVisitText}>
              You were last on Maatra on {formattedLastVisitDate}
            </div>
            <div className={styles.questionContainer}>
              <div className={styles.iconContainer}>
                <IconBulb size={32} color="#8764BB" />
              </div>
              <div className={styles.questionText}>
                Have there been any health updates since then?
              </div>
            </div>

            <div className={styles.infoSection}>
              <div className={styles.infoIconContainer}>
                <IconInfoCircle size={24} color="#6B6B6B" />
              </div>
              <div className={styles.infoText}>
                Add updates for anyone in your family. Maatra will place them in
                the right profile.
              </div>
            </div>

            <div className={styles.cardsContainer}>
              {updateCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <div key={index} className={styles.updateCard}>
                    <div className={styles.cardIconContainer}>
                      <Icon size={16} color="#45168C" />
                    </div>
                    <div className={styles.cardContent}>
                      <div className={styles.cardTitle}>{card.title}</div>
                      <MButton
                        text={card.cta}
                        variant="link"
                        size="md"
                        className={styles.cardLinkButton}
                        onClick={handleAddDocument}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.skipContainer}>
              <MButton
                text="Skip for now"
                variant="outline"
                size="md"
                onClick={handleDismiss}
                style={{ color: "#2A2A2A" }}
              />
            </div>
          </>
        )}

        <MDocumentUploadOptions
          open={showUploadModal}
          onClose={handleUploadModalClose}
        />
      </div>
    </>
  );
};

export { Nudge };
